import type { ColumnDef, DataGridCellAddr, DataGridRow } from "../types";

export type MoveDirection = "up" | "down" | "left" | "right" | "nextWrap" | "prevWrap";

export const buildNavigationMatrix = <TData = unknown>(
	rows: DataGridRow<TData>[],
	visibleColumns: ColumnDef<TData>[],
	expanded: Set<string>,
): DataGridCellAddr[][] => {
	const navigableCols = visibleColumns.filter((c) => c.navigable !== false);
	if (navigableCols.length === 0 || rows.length === 0) return [];

	const matrix: DataGridCellAddr[][] = [];

	const emitRow = (rowId: string) => {
		matrix.push(navigableCols.map((col) => ({ rowId, colId: col.id })));
	};

	const walk = (row: DataGridRow<TData>) => {
		const children = row.children ?? [];
		const isExpandable = children.length > 0;
		const isExpanded = row.alwaysExpanded ? isExpandable : expanded.has(row.id);

		if (row.navigable !== false) {
			emitRow(row.id);
		}

		if (!isExpandable || !isExpanded) return;
		for (const child of children) walk(child);
	};

	for (const row of rows) walk(row);
	return matrix;
};

export const findCellPosition = (
	matrix: DataGridCellAddr[][],
	cell: DataGridCellAddr,
): { row: number; col: number } | null => {
	for (let r = 0; r < matrix.length; r++) {
		for (let c = 0; c < matrix[r].length; c++) {
			const m = matrix[r][c];
			if (m.rowId === cell.rowId && m.colId === cell.colId) return { row: r, col: c };
		}
	}
	return null;
};

export const cellAddrEquals = (a: DataGridCellAddr, b: DataGridCellAddr): boolean =>
	a.rowId === b.rowId && a.colId === b.colId;

export const computeNextCell = (
	matrix: DataGridCellAddr[][],
	from: DataGridCellAddr,
	direction: MoveDirection,
): DataGridCellAddr | null => {
	if (matrix.length === 0) return null;
	const pos = findCellPosition(matrix, from);
	if (!pos) return matrix[0]?.[0] ?? null;

	const lastRow = matrix.length - 1;
	const lastCol = matrix[pos.row].length - 1;

	switch (direction) {
		case "up":
			return pos.row > 0 ? matrix[pos.row - 1][pos.col] : from;
		case "down":
			return pos.row < lastRow ? matrix[pos.row + 1][pos.col] : from;
		case "left":
			return pos.col > 0 ? matrix[pos.row][pos.col - 1] : from;
		case "right":
			return pos.col < lastCol ? matrix[pos.row][pos.col + 1] : from;
		case "nextWrap": {
			if (pos.col < lastCol) return matrix[pos.row][pos.col + 1];
			if (pos.row < lastRow) return matrix[pos.row + 1][0];
			return matrix[0][0];
		}
		case "prevWrap": {
			if (pos.col > 0) return matrix[pos.row][pos.col - 1];
			if (pos.row > 0) {
				const prev = matrix[pos.row - 1];
				return prev[prev.length - 1];
			}
			const last = matrix[matrix.length - 1];
			return last[last.length - 1];
		}
	}
};
